import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithRedirect,
  linkWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
} from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '../firebase';

const isNativePlatform = () =>
  typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

// ── Nativer Auth-Layer (@capacitor-firebase/authentication, skipNativeAuth:false) ──
// Auf Native macht das Plugin den ECHTEN nativen Firebase-Login (iOS-SDK via
// URLSession) — kein WKWebView-fetch, kein signInWithCredential (das schlug mit
// auth/network-request-failed fehl), kein localStorage-Inject-Hack. Firestore
// läuft auf Native über das native Plugin (siehe lib/fsCompat.js), das die native
// Auth-Session nutzt. Der hier gebaute `user`-Proxy bildet die JS-SDK-User-API
// nach, die die App liest (uid/email/displayName/photoURL/metadata + getIdToken/
// getIdTokenResult), damit der restliche Code unverändert bleibt.
const loadAuthPlugin = () => import('@capacitor-firebase/authentication');

function mapNativeUser(pUser) {
  if (!pUser) return null;
  const created = pUser.metadata?.creationTime;
  const lastSignIn = pUser.metadata?.lastSignInTime;
  return {
    uid: pUser.uid,
    email: pUser.email ?? null,
    displayName: pUser.displayName ?? null,
    photoURL: pUser.photoUrl ?? null,
    emailVerified: !!pUser.emailVerified,
    isAnonymous: !!pUser.isAnonymous,
    phoneNumber: pUser.phoneNumber ?? null,
    providerData: (pUser.providerData || []).map((p) => ({
      providerId: p.providerId,
      uid: p.uid,
      email: p.email ?? null,
      displayName: p.displayName ?? null,
      photoURL: p.photoUrl ?? null,
      phoneNumber: null,
    })),
    metadata: {
      creationTime: created != null ? new Date(created).toUTCString() : undefined,
      lastSignInTime: lastSignIn != null ? new Date(lastSignIn).toUTCString() : undefined,
    },
    getIdToken: async (forceRefresh = false) => {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      const r = await FirebaseAuthentication.getIdToken(forceRefresh ? { forceRefresh: true } : undefined);
      return r?.token;
    },
    getIdTokenResult: async (forceRefresh = false) => {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      const r = await FirebaseAuthentication.getIdTokenResult({ forceRefresh: !!forceRefresh });
      return { claims: r?.claims || {} };
    },
  };
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectError, setRedirectError] = useState('');

  useEffect(() => {
    // ── Native: Auth-State über das Plugin ───────────────────────────────────
    if (isNativePlatform()) {
      let handle;
      let cancelled = false;
      (async () => {
        const { FirebaseAuthentication } = await loadAuthPlugin();
        try {
          const { user: cur } = await FirebaseAuthentication.getCurrentUser();
          if (!cancelled) setUser(mapNativeUser(cur));
        } catch (_) { /* getCurrentUser optional */ }
        if (!cancelled) setLoading(false);
        handle = await FirebaseAuthentication.addListener('authStateChange', (change) => {
          setUser(mapNativeUser(change?.user || null));
          setLoading(false);
        });
      })();
      return () => { cancelled = true; try { handle && handle.remove(); } catch (_) {} };
    }

    // ── Web: JS-SDK Redirect-Flow ─────────────────────────────────────────────
    getRedirectResult(auth)
      .then((res) => {
        let pending = null;
        try { pending = localStorage.getItem('red_redir'); localStorage.removeItem('red_redir'); } catch (_) {}
        if (pending && !res) setRedirectError('empty');
      })
      .catch((e) => {
        try { localStorage.removeItem('red_redir'); } catch (_) {}
        setRedirectError(e?.code || e?.message || 'failed');
        console.warn('[auth] getRedirectResult:', e?.code || e?.message);
      });
    const timeout = setTimeout(() => setLoading(false), 6000);
    const unsub = onAuthStateChanged(auth, (u) => {
      clearTimeout(timeout);
      setUser(u);
      setLoading(false);
    });
    return () => { unsub(); clearTimeout(timeout); };
  }, []);

  // ── Google Sign-In ───────────────────────────────────────────────────────
  // Native: Plugin macht den nativen Login; authStateChange aktualisiert `user`.
  // Web: signInWithRedirect (Popup hängt mit Custom-Auth-Domain auth.redcrypto.de).
  const signInWithGoogle = async () => {
    if (isNativePlatform()) {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      const res = await FirebaseAuthentication.signInWithGoogle();
      if (res?.user) setUser(mapNativeUser(res.user));
      return;
    }
    try { localStorage.setItem('red_redir', '1'); } catch (_) {}
    return signInWithRedirect(auth, googleProvider);
  };

  // ── Apple Sign-In ──────────────────────────────────────────────────────────
  const signInWithApple = async () => {
    if (isNativePlatform()) {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      const res = await FirebaseAuthentication.signInWithApple();
      if (res?.user) setUser(mapNativeUser(res.user));
      return;
    }
    try { localStorage.setItem('red_redir', '1'); } catch (_) {}
    return signInWithRedirect(auth, appleProvider);
  };

  // ── Apple verknüpfen (eingeloggter User verknüpft Apple-Provider) ──────────
  const linkWithApple = async () => {
    if (isNativePlatform()) {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      await FirebaseAuthentication.linkWithApple();
      try {
        const { user: cur } = await FirebaseAuthentication.getCurrentUser();
        setUser(mapNativeUser(cur));
      } catch (_) { /* refresh optional */ }
      return;
    }
    try { localStorage.setItem('red_redir', '1'); } catch (_) {}
    return linkWithRedirect(auth.currentUser, appleProvider);
  };

  // ── E-Mail/Passwort ────────────────────────────────────────────────────────
  const signInWithEmail = async (email, password) => {
    if (isNativePlatform()) {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      const res = await FirebaseAuthentication.signInWithEmailAndPassword({ email, password });
      if (res?.user) setUser(mapNativeUser(res.user));
      return;
    }
    return signInWithEmailAndPassword(auth, email, password);
  };
  const signUpWithEmail = async (email, password) => {
    if (isNativePlatform()) {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      const res = await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password });
      if (res?.user) setUser(mapNativeUser(res.user));
      return;
    }
    return createUserWithEmailAndPassword(auth, email, password);
  };
  const resetPassword = async (email) => {
    if (isNativePlatform()) {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      return FirebaseAuthentication.sendPasswordResetEmail({ email });
    }
    return sendPasswordResetEmail(auth, email);
  };
  const signOut = async () => {
    if (isNativePlatform()) {
      const { FirebaseAuthentication } = await loadAuthPlugin();
      await FirebaseAuthentication.signOut();
      setUser(null);
      return;
    }
    return fbSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, redirectError, signInWithGoogle, signInWithApple, linkWithApple, signInWithEmail, signUpWithEmail, resetPassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
